import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Wallet, X, AlertTriangle, Coins, ArrowDownUp,
  ArrowDownToLine, ArrowUpFromLine, QrCode, Copy, CheckCircle, Zap, ExternalLink
} from 'lucide-react';
import { useGameWallet } from '../providers/GameWalletProvider';
import { useAuth } from '../providers/AuthProvider';
import { toast } from 'sonner';
import JupiterSwapWidget from './JupiterSwapWidget';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';

// 5% Service Fee for deposits and withdrawals
const SERVICE_FEE_PERCENT = 5;

// Local Wallet Icon
const LOCAL_WALLET_ICON = (
  <div className="w-full h-full rounded-lg bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center">
    <Zap className="w-5 h-5 text-white" />
  </div>
);

const ProminentWalletDisplay = ({ onOpenSolanaWallet, compact = false }) => {
  const navigate = useNavigate();
  // CRITICAL: Use slapBalance (blockchain) directly, NOT credits (which may be from server)
  const { slapBalance, isWalletActive, refreshCredits, connectionStatus, fetchSlapTokenBalance } = useGameWallet();
  const { solanaAddress, localWallet } = useAuth();
  const [showWalletModal, setShowWalletModal] = useState(false);

  // Use BLOCKCHAIN balance (slapBalance), fallback to 0
  const displayBalance = slapBalance || 0;

  // Get the REAL Solana address from AuthProvider
  const fullSolanaAddress = solanaAddress || localWallet?.publicKey;
  const shortAddress = fullSolanaAddress 
    ? `${fullSolanaAddress.slice(0, 4)}...${fullSolanaAddress.slice(-4)}`
    : null;

  return (
    <>
      {/* Main Wallet Display - PROMINENT & BIGGEST ELEMENT */}
      <motion.div
        className={`flex items-center gap-2 sm:gap-3 px-3 sm:px-5 py-2 sm:py-2.5 rounded-xl bg-gradient-to-r from-yellow-900/50 to-amber-900/50 border-2 border-yellow-400 shadow-xl shadow-yellow-500/30 cursor-pointer hover:border-yellow-300 hover:shadow-yellow-500/50 transition-all ${
          compact ? 'px-2 py-1.5' : ''
        }`}
        onClick={() => setShowWalletModal(true)}
        whileTap={{ scale: 0.98 }}
        animate={{
          boxShadow: [
            '0 10px 40px rgba(234,179,8,0.3)',
            '0 10px 50px rgba(234,179,8,0.5)',
            '0 10px 40px rgba(234,179,8,0.3)',
          ]
        }}
        transition={{ duration: 2, repeat: Infinity }}
        data-testid="prominent-wallet-display"
      >
        {/* Local Wallet Icon */}
        <div className="relative">
          <div className="w-9 h-9 sm:w-11 sm:h-11 rounded-xl overflow-hidden shadow-lg p-1">
            {LOCAL_WALLET_ICON}
          </div>
          {isWalletActive && (
            <motion.div
              className="absolute -top-1 -right-1 w-3 h-3 sm:w-3.5 sm:h-3.5 rounded-full bg-green-400 border-2 border-black"
              animate={{ scale: [1, 1.3, 1] }}
              transition={{ duration: 2, repeat: Infinity }}
            />
          )}
        </div>
        
        {/* Balance - LARGE */}
        <div className="flex flex-col">
          <span className="text-[9px] sm:text-[11px] text-yellow-400/80 uppercase tracking-wider font-bold">$SLAP</span>
          <div className="flex items-baseline gap-0.5 sm:gap-1">
            <span className="text-xl sm:text-3xl font-black text-yellow-300 leading-none drop-shadow-lg">
              {connectionStatus === 'connecting' ? '---' : Math.floor(displayBalance).toLocaleString()}
            </span>
            <Coins className="w-3.5 h-3.5 sm:w-5 sm:h-5 text-yellow-400" />
          </div>
        </div>
      </motion.div>

      {/* Wallet Modal with 3 Tabs */}
      <AnimatePresence>
        {showWalletModal && (
          <WalletModal
            onClose={() => setShowWalletModal(false)}
            onOpenSolanaWallet={onOpenSolanaWallet}
            currentBalance={displayBalance}
            refreshCredits={refreshCredits}
            solanaAddress={fullSolanaAddress}
          />
        )}
      </AnimatePresence>
    </>
  );
};

// 3-Tab Wallet Modal with PROPER CLOSE BUTTONS
const WalletModal = ({ onClose, onOpenSolanaWallet, currentBalance, refreshCredits, solanaAddress }) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('deposit');
  const [showSwapWidget, setShowSwapWidget] = useState(false);

  // Get short and full address
  const fullAddress = solanaAddress || 'Not created';
  const shortAddress = solanaAddress 
    ? `${solanaAddress.slice(0, 8)}...${solanaAddress.slice(-8)}`
    : 'Not created';

  const tabs = [
    { id: 'deposit', label: 'DEPOSIT', icon: ArrowDownToLine, color: 'green' },
    { id: 'swap', label: 'SWAP', icon: ArrowDownUp, color: 'purple' },
    { id: 'withdraw', label: 'WITHDRAW', icon: ArrowUpFromLine, color: 'red' },
  ];

  const goToFullWallet = () => {
    onClose();
    navigate('/wallet');
  };

  return createPortal(
    <motion.div
      className="fixed inset-0 z-[9998] flex items-center justify-center bg-black/85 p-4"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
      data-testid="wallet-modal-backdrop"
    >
      <motion.div
        className="bg-[#0d0d0d] border-2 border-yellow-500/50 rounded-2xl w-full max-w-md overflow-hidden max-h-[90vh] flex flex-col relative"
        initial={{ scale: 0.9, opacity: 0, y: 20 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        exit={{ scale: 0.9, opacity: 0, y: 20 }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* FIX: RED X CLOSE BUTTON - Top Right Absolute */}
        <button
          onClick={onClose}
          className="absolute top-3 right-3 p-2 bg-red-500/20 hover:bg-red-500/40 rounded-full transition-colors z-20"
          data-testid="wallet-modal-close-x"
        >
          <X className="w-6 h-6 text-red-400" />
        </button>

        {/* Header */}
        <div className="flex items-center gap-3 p-4 border-b border-white/10 shrink-0 pr-14">
          <div className="w-10 h-10 rounded-xl overflow-hidden">
            {LOCAL_WALLET_ICON}
          </div>
          <div>
            <h3 className="font-bold text-white text-lg">YOUR WALLET</h3>
            <p className="text-yellow-400 text-sm font-bold">{currentBalance.toLocaleString()} $SLAP</p>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="flex border-b border-white/10 shrink-0">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex-1 py-3 px-2 flex items-center justify-center gap-2 transition-all ${
                  isActive 
                    ? `bg-${tab.color}-500/20 border-b-2 border-${tab.color}-400` 
                    : 'hover:bg-white/5'
                }`}
                style={isActive ? {
                  backgroundColor: tab.color === 'green' ? 'rgba(34,197,94,0.2)' : 
                                   tab.color === 'purple' ? 'rgba(168,85,247,0.2)' : 
                                   'rgba(239,68,68,0.2)',
                  borderBottom: `2px solid ${tab.color === 'green' ? '#22c55e' : 
                                              tab.color === 'purple' ? '#a855f7' : 
                                              '#ef4444'}`
                } : {}}
                data-testid={`wallet-tab-${tab.id}`}
              >
                <Icon className={`w-4 h-4 ${
                  isActive 
                    ? tab.color === 'green' ? 'text-green-400' : 
                      tab.color === 'purple' ? 'text-purple-400' : 'text-red-400'
                    : 'text-white/50'
                }`} />
                <span className={`text-xs font-bold ${isActive ? 'text-white' : 'text-white/50'}`}>
                  {tab.label}
                </span>
              </button>
            );
          })}
        </div>

        {/* Tab Content - Scrollable */}
        <div className="p-4 overflow-y-auto flex-1">
          {activeTab === 'deposit' && (
            <DepositTab 
              onOpenSolanaWallet={() => { onClose(); onOpenSolanaWallet?.(); }} 
              solanaAddress={fullAddress}
              shortAddress={shortAddress}
            />
          )}
          {activeTab === 'swap' && (
            <SwapTab onOpenWidget={() => { setShowSwapWidget(true); }} />
          )}
          {activeTab === 'withdraw' && (
            <WithdrawTab currentBalance={currentBalance} />
          )}
        </div>

        {/* FIX: BOTTOM BUTTONS - Always visible */}
        <div className="p-4 border-t border-white/10 shrink-0 space-y-2">
          {/* Open Full Wallet Button */}
          <button
            onClick={goToFullWallet}
            className="w-full py-3 bg-green-500/20 hover:bg-green-500/30 border border-green-500/50 rounded-xl font-bold text-green-400 transition-colors flex items-center justify-center gap-2"
            data-testid="open-full-wallet-btn"
          >
            <ExternalLink className="w-5 h-5" />
            OPEN FULL WALLET
          </button>
          
          {/* Close Button */}
          <button
            onClick={onClose}
            className="w-full py-3 bg-red-500/20 hover:bg-red-500/30 border border-red-500/50 rounded-xl font-bold text-red-400 transition-colors flex items-center justify-center gap-2"
            data-testid="wallet-modal-close-bottom"
          >
            <X className="w-5 h-5" />
            CLOSE
          </button>
        </div>
      </motion.div>

      {/* Jupiter Swap Widget */}
      <JupiterSwapWidget
        isOpen={showSwapWidget}
        onClose={() => setShowSwapWidget(false)}
        onSwapComplete={() => {
          refreshCredits?.();
          toast.success('Swap complete!');
        }}
      />
    </motion.div>,
    document.body
  );
};

// Deposit Tab - Shows REAL Solana Address
const DepositTab = ({ onOpenSolanaWallet, solanaAddress, shortAddress }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(solanaAddress || 'Not created');
    setCopied(true);
    toast.success('Solana address copied!');
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="space-y-4">
      {/* QR Code Placeholder - Real address */}
      <div className="bg-white rounded-xl p-4 mx-auto w-fit">
        <div className="w-32 h-32 bg-gray-200 rounded-lg flex items-center justify-center">
          <QrCode className="w-16 h-16 text-gray-400" />
        </div>
      </div>

      {/* Real Solana Badge */}
      <div className="flex justify-center">
        <span className="px-3 py-1 bg-green-500/20 text-green-400 text-xs font-bold rounded-full border border-green-500/30">
          ✓ REAL SOLANA ADDRESS
        </span>
      </div>

      {/* FULL Solana Address */}
      <div className="bg-black/50 rounded-xl p-3 border border-green-500/30">
        <p className="text-green-400 text-xs mb-1 font-bold">YOUR SOLANA ADDRESS</p>
        <div className="flex items-start justify-between gap-2">
          <code className="text-white font-mono text-xs break-all flex-1 bg-black/30 p-2 rounded">
            {solanaAddress || 'Not created'}
          </code>
          <button
            onClick={handleCopy}
            className="p-2 hover:bg-white/10 rounded-lg transition-colors shrink-0"
          >
            {copied ? (
              <CheckCircle className="w-4 h-4 text-green-400" />
            ) : (
              <Copy className="w-4 h-4 text-white/60" />
            )}
          </button>
        </div>
        <p className="text-white/40 text-xs mt-2">Short: {shortAddress}</p>
      </div>

      {/* Fee Notice */}
      <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-3">
        <div className="flex items-center gap-2 text-yellow-400 text-sm font-bold">
          <AlertTriangle className="w-4 h-4" />
          Service Fee: {SERVICE_FEE_PERCENT}%
        </div>
        <p className="text-yellow-200/70 text-xs mt-1">
          A {SERVICE_FEE_PERCENT}% fee applies to all deposits.
        </p>
      </div>

      {/* Open Full Wallet */}
      <button
        onClick={onOpenSolanaWallet}
        className="w-full py-3 bg-gradient-to-r from-green-600 to-emerald-600 rounded-xl font-bold text-white hover:from-green-500 hover:to-emerald-500 transition-all"
      >
        Open Full Wallet Manager
      </button>
    </div>
  );
};

// Swap Tab
const SwapTab = ({ onOpenWidget }) => {
  return (
    <div className="space-y-4">
      {/* Token Preview */}
      <div className="bg-black/30 rounded-xl p-4 border border-white/10">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center text-xl">
              ◎
            </div>
            <div>
              <div className="text-white font-bold">SOL</div>
              <div className="text-white/50 text-xs">Solana</div>
            </div>
          </div>
          <ArrowDownUp className="w-6 h-6 text-white/30" />
          <div className="flex items-center gap-3">
            <div>
              <div className="text-white font-bold text-right">$SLAP</div>
              <div className="text-white/50 text-xs text-right">Game Token</div>
            </div>
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-green-500 to-emerald-500 flex items-center justify-center text-xl">
              🐸
            </div>
          </div>
        </div>
      </div>

      {/* Fee Notice */}
      <div className="bg-purple-500/10 border border-purple-500/30 rounded-xl p-3">
        <div className="flex items-center gap-2 text-purple-400 text-sm font-bold">
          <AlertTriangle className="w-4 h-4" />
          Platform Fee: 1%
        </div>
        <p className="text-purple-200/70 text-xs mt-1">
          Swaps are powered by Jupiter with a 1% platform fee.
        </p>
      </div>

      {/* Swap Button */}
      <button
        onClick={onOpenWidget}
        className="w-full py-4 bg-gradient-to-r from-purple-600 to-blue-600 rounded-xl font-bold text-white hover:from-purple-500 hover:to-blue-500 transition-all flex items-center justify-center gap-2"
      >
        <ArrowDownUp className="w-5 h-5" />
        OPEN SWAP WIDGET
      </button>
    </div>
  );
};

// Withdraw Tab
const WithdrawTab = ({ currentBalance }) => {
  const [amount, setAmount] = useState('');
  const { withdraw } = useGameWallet();
  const [isProcessing, setIsProcessing] = useState(false);

  const numAmount = parseFloat(amount) || 0;
  const fee = numAmount * (SERVICE_FEE_PERCENT / 100);
  const finalAmount = numAmount - fee;

  const handleWithdraw = async () => {
    if (numAmount <= 0) {
      toast.error('Enter a valid amount');
      return;
    }
    if (numAmount > currentBalance) {
      toast.error('Insufficient balance');
      return;
    }

    setIsProcessing(true);
    try {
      await withdraw?.(numAmount);
      toast.success(`Withdrawn ${finalAmount.toFixed(0)} $SLAP worth!`, {
        description: `Fee: ${fee.toFixed(0)} (${SERVICE_FEE_PERCENT}%)`
      });
      setAmount('');
    } catch (e) {
      toast.error('Withdrawal failed');
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Amount Input */}
      <div>
        <label className="text-white/60 text-sm mb-2 block">Amount ($SLAP)</label>
        <input
          type="number"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          placeholder="0"
          className="w-full bg-black/50 border border-white/20 rounded-xl p-3 text-white text-xl font-bold placeholder:text-white/30 focus:border-red-500 focus:outline-none"
        />
        <p className="text-white/40 text-xs mt-1">Available: {currentBalance.toLocaleString()} $SLAP</p>
      </div>

      {/* Quick Amounts */}
      <div className="grid grid-cols-4 gap-2">
        {[100, 500, 1000, currentBalance].map((val, i) => (
          <button
            key={i}
            onClick={() => setAmount(val.toString())}
            className="py-2 bg-white/5 hover:bg-white/10 rounded-lg text-white/80 text-xs font-bold transition-colors"
          >
            {i === 3 ? 'MAX' : val}
          </button>
        ))}
      </div>

      {/* Fee Breakdown */}
      {numAmount > 0 && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-3 text-sm">
          <div className="flex justify-between text-white/60">
            <span>You withdraw:</span>
            <span>{numAmount.toFixed(0)} $SLAP</span>
          </div>
          <div className="flex justify-between text-red-400">
            <span>Fee ({SERVICE_FEE_PERCENT}%):</span>
            <span>-{fee.toFixed(0)}</span>
          </div>
          <div className="flex justify-between text-green-400 font-bold border-t border-white/10 pt-1 mt-1">
            <span>You receive:</span>
            <span>{finalAmount.toFixed(0)} SOL worth</span>
          </div>
        </div>
      )}

      {/* Withdraw Button */}
      <button
        onClick={handleWithdraw}
        disabled={isProcessing || numAmount <= 0}
        className="w-full py-4 bg-gradient-to-r from-red-600 to-orange-600 rounded-xl font-bold text-white hover:from-red-500 hover:to-orange-500 transition-all disabled:opacity-50"
      >
        {isProcessing ? 'Processing...' : 'WITHDRAW NOW'}
      </button>
    </div>
  );
};

export default ProminentWalletDisplay;
